/** Financial-year month order for investment / proof windows (Apr → Mar) */
export const FY_INVESTMENT_MONTHS = [
  { num: 4, label: "April" },
  { num: 5, label: "May" },
  { num: 6, label: "June" },
  { num: 7, label: "July" },
  { num: 8, label: "August" },
  { num: 9, label: "September" },
  { num: 10, label: "October" },
  { num: 11, label: "November" },
  { num: 12, label: "December" },
  { num: 1, label: "January" },
  { num: 2, label: "February" },
  { num: 3, label: "March" },
];

export const DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => i + 1);

/** investment: New joinees | proof: Exited */
export const WINDOW_KINDS = {
  investment: {
    primaryFlag: "allowNewJoinees",
    primaryLabel: "New joinees",
    primaryAudienceKey: "new_joinees",
  },
  proof: {
    primaryFlag: "allowExited",
    primaryLabel: "Exited",
    primaryAudienceKey: "exited",
  },
};

/** @deprecated Kept for HMR/compat — prefer normalizeAudienceFlags */
export const normalizeMonthScope = (raw) => {
  const scope = String(raw || "all").toLowerCase();
  if (
    scope === "new_joinees" ||
    scope === "exited" ||
    scope === "selection"
  ) {
    return scope;
  }
  return "all";
};

const clampDay = (value, fallback = 1) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return fallback;
  return Math.min(31, Math.max(1, Math.floor(n)));
};

const dayFromLegacyDate = (dateStr) => {
  if (!dateStr) return null;
  const m = String(dateStr).match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return clampDay(m[3]);
};

/**
 * Independent audience flags (combinable).
 * kind: "investment" → allowNewJoinees | kind: "proof" → allowExited
 */
export const normalizeAudienceFlags = (
  cfg = {},
  legacyScope = null,
  kind = "investment"
) => {
  const meta = WINDOW_KINDS[kind] || WINDOW_KINDS.investment;
  const primaryFlag = meta.primaryFlag;
  const primaryAudienceKey = meta.primaryAudienceKey;

  const hasExplicit =
    cfg[primaryFlag] != null ||
    cfg.allowNewJoinees != null ||
    cfg.allowExited != null ||
    cfg.allowAll != null ||
    cfg.allowSelection != null ||
    Array.isArray(cfg.audiences);

  if (hasExplicit) {
    let allowPrimary = !!(cfg[primaryFlag] ?? cfg.allowNewJoinees ?? cfg.allowExited);
    let allowAll = !!cfg.allowAll;
    let allowSelection = !!cfg.allowSelection;
    if (Array.isArray(cfg.audiences)) {
      const set = new Set(cfg.audiences.map((a) => String(a).toLowerCase()));
      if (
        set.has(primaryAudienceKey) ||
        set.has("new_joinees") ||
        set.has("exited")
      ) {
        allowPrimary = true;
      }
      if (set.has("all")) allowAll = true;
      if (set.has("selection")) allowSelection = true;
    }
    if (!allowPrimary && !allowAll && !allowSelection) {
      allowAll = true;
    }
    return {
      [primaryFlag]: allowPrimary,
      allowAll,
      allowSelection,
    };
  }

  const scope = String(cfg.scope || legacyScope || "all").toLowerCase();
  return {
    [primaryFlag]:
      scope === primaryAudienceKey ||
      scope === "new_joinees" ||
      scope === "exited",
    allowAll: scope === "all" || !scope,
    allowSelection: scope === "selection",
  };
};

export const createDefaultWindows = (kind = "investment") => {
  const meta = WINDOW_KINDS[kind] || WINDOW_KINDS.investment;
  const months = {};
  FY_INVESTMENT_MONTHS.forEach(({ num }) => {
    months[num] = {
      enabled: false,
      fromDay: 1,
      toDay: 31,
      [meta.primaryFlag]: false,
      allowAll: true,
      allowSelection: false,
      selectedEmployeeIds: [],
    };
  });
  return { months };
};

export const createDefaultInvestmentWindows = () =>
  createDefaultWindows("investment");

export const createDefaultProofWindows = () => createDefaultWindows("proof");

export const normalizeWindowsFromSettings = (raw, kind = "investment") => {
  const base = createDefaultWindows(kind);
  if (!raw) return base;
  const parsed =
    typeof raw === "string"
      ? (() => {
          try {
            return JSON.parse(raw);
          } catch {
            return null;
          }
        })()
      : raw;
  if (!parsed || typeof parsed !== "object") return base;

  const legacyScope =
    parsed.scope != null ? String(parsed.scope).toLowerCase() : null;
  const legacyIds = Array.isArray(parsed.selectedEmployeeIds)
    ? [
        ...new Set(
          parsed.selectedEmployeeIds
            .map((id) => Number(id))
            .filter((n) => n > 0)
        ),
      ]
    : [];

  const monthsIn = parsed.months || {};
  FY_INVESTMENT_MONTHS.forEach(({ num }) => {
    const cfg = monthsIn[num] || monthsIn[String(num)] || {};
    const flags = normalizeAudienceFlags(cfg, legacyScope, kind);
    const ids = Array.isArray(cfg.selectedEmployeeIds)
      ? [
          ...new Set(
            cfg.selectedEmployeeIds
              .map((id) => Number(id))
              .filter((n) => n > 0)
          ),
        ]
      : flags.allowSelection
        ? legacyIds
        : [];

    let fromDay =
      cfg.fromDay != null
        ? clampDay(cfg.fromDay, 1)
        : dayFromLegacyDate(cfg.fromDate) || 1;
    let toDay =
      cfg.toDay != null
        ? clampDay(cfg.toDay, fromDay)
        : dayFromLegacyDate(cfg.toDate) || 31;
    if (toDay < fromDay) toDay = fromDay;

    base.months[num] = {
      enabled: !!cfg.enabled,
      fromDay,
      toDay,
      ...flags,
      selectedEmployeeIds: ids,
    };
  });
  return base;
};

export const buildWindowsPayload = (windows, kind = "investment") => {
  if (!windows) return null;
  const meta = WINDOW_KINDS[kind] || WINDOW_KINDS.investment;
  const months = {};
  FY_INVESTMENT_MONTHS.forEach(({ num }) => {
    const cfg = windows.months?.[num] || {};
    const flags = normalizeAudienceFlags(cfg, null, kind);
    const fromDay = clampDay(cfg.fromDay, 1);
    let toDay = clampDay(cfg.toDay, fromDay);
    if (toDay < fromDay) toDay = fromDay;
    months[num] = {
      enabled: !!cfg.enabled,
      fromDay: cfg.enabled ? fromDay : null,
      toDay: cfg.enabled ? toDay : null,
      [meta.primaryFlag]: cfg.enabled ? !!flags[meta.primaryFlag] : false,
      allowAll: cfg.enabled ? !!flags.allowAll : false,
      allowSelection: cfg.enabled ? !!flags.allowSelection : false,
      selectedEmployeeIds:
        cfg.enabled &&
        flags.allowSelection &&
        Array.isArray(cfg.selectedEmployeeIds)
          ? cfg.selectedEmployeeIds
          : [],
    };
  });
  return { months };
};

export const buildInvestmentWindowsPayload = (windows) =>
  buildWindowsPayload(windows, "investment");

export const buildProofWindowsPayload = (windows) =>
  buildWindowsPayload(windows, "proof");

export const validateWindows = (windows, kind = "investment") => {
  const meta = WINDOW_KINDS[kind] || WINDOW_KINDS.investment;
  const enabledMonths = FY_INVESTMENT_MONTHS.filter(
    ({ num }) => windows?.months?.[num]?.enabled
  );
  if (!enabledMonths.length) {
    return {
      ok: false,
      message: "Enable at least one month (April–March) with From/To day",
    };
  }
  for (const { num, label } of enabledMonths) {
    const cfg = windows.months[num];
    const fromDay = Number(cfg.fromDay);
    const toDay = Number(cfg.toDay);
    if (!fromDay || !toDay) {
      return { ok: false, message: `Set From and To day for ${label}` };
    }
    if (toDay < fromDay) {
      return {
        ok: false,
        message: `${label}: To day cannot be before From day`,
      };
    }
    const flags = normalizeAudienceFlags(cfg, null, kind);
    if (
      !flags[meta.primaryFlag] &&
      !flags.allowAll &&
      !flags.allowSelection
    ) {
      return {
        ok: false,
        message: `${label}: Select at least one of ${meta.primaryLabel}, Enable all, or Selection`,
      };
    }
    if (flags.allowSelection && !(cfg.selectedEmployeeIds || []).length) {
      return {
        ok: false,
        message: `${label}: Selection requires at least one employee`,
      };
    }
  }
  return { ok: true };
};

export const validateInvestmentWindows = (windows) =>
  validateWindows(windows, "investment");

export const validateProofWindows = (windows) =>
  validateWindows(windows, "proof");

export const getMonthName = (monthNumber) => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return months[parseInt(monthNumber, 10) - 1] || "January";
};
